import { useEffect, useRef } from 'react';

import isNil from 'lodash/isNil';

import { dateHelpers } from '@nocowanie/core/helpers';

import { IPageVisitPayload } from '@noc-sm-plugin';

import { useNocSmPageVisitEvent } from '@app/hooks/sales-manago';
import { selectors as accommodationSelectors } from '@app/state/slices/accommodation.slice';
import { selectors as accommodationStaticSelectors } from '@app/state/slices/accommodation-static.slice';
import { useAppSelector } from '@app/state/state';

export const useNocSmAccommodationPageVisit = () => {
    const filters = useAppSelector(accommodationSelectors.selectFilters);
    const accommodation = useAppSelector(accommodationStaticSelectors.selectAccommodation);
    const bestOffer = useAppSelector(accommodationSelectors.selectBestOffer);
    const eventPayload = useRef<IPageVisitPayload | undefined>();
    const completed = useRef<boolean>(false);
    const { pushPageVisitEvent } = useNocSmPageVisitEvent();

    /**
     * Track changes in filters and prepare correct payload
     */
    useEffect(() => {
        if (isNil(filters) || isNil(accommodation)) {
            return;
        }

        const { guestsCount, childrenCount, checkIn, checkOut, id } = filters;
        const {
            type,
            location,
            features: { internalAmenityGroups },
        } = accommodation;

        eventPayload.current = {
            products: id?.replace(/^\D+/g, ''),
            value: `${bestOffer?.totalPrice ?? ''}`,
            /**
             * Based on business requirements array order should be:
             *
             * 1) detail[1] - event name (“product_view”)
             * 2) detail[2] - accommodation city
             * 3) detail[3] - accommodation region (e.g. seaside, Mazury, Tatry etc. - all applicable)
             * 4) detail[4] - accommodation category
             * 5) detail[5] - stay dates (“DD-MONTH-YYYY - DD-MONTH-YYYY” format)
             * 6) detail[6] - guests (“X_adults, Y_children” format)
             * 7) detail[7] - top amenities
             */
            details: [
                'product_view',
                location?.settlement?.name,
                (location?.regions ?? []).map(region => region.name).join(', '),
                `${type}`,
                dateHelpers.formatStayDatesForSalesManago(checkIn, checkOut),
                `${guestsCount}_adults, ${childrenCount}_children`,
                (internalAmenityGroups ?? []).map(group => group.label).join(', '),
            ],
        };
    }, [filters, accommodation, bestOffer]);

    /**
     * Send event to NocSmPlugin on a component unmounted
     */
    useEffect(() => {
        const handler = () => {
            if (!isNil(eventPayload.current) && !completed.current) {
                completed.current = true;
                pushPageVisitEvent(eventPayload.current);
            }
        };

        window.addEventListener('beforeunload', handler);

        return () => {
            window.removeEventListener('beforeunload', handler);
            handler();
        };
    }, []);
};
